import { type FC } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Image } from '@chakra-ui/react';
import { ChallengeRequestScreenState } from '@shinetools/sca-operations';
import {
  Button,
  Typography,
  View,
  YStack,
} from '@shinetools/sunshine-universal';

import {
  ChallengeStateName,
  type VerifyChallengeOutput,
} from '__generated__/GQL';
import OkHand from 'assets/brand/ok-hand.png';
import SunshineModal from 'components/_core/SunshineModal';
import Loader from 'components/Loader';
import { useVerifyChallenge } from 'components/StrongAuthenticationChallengeModal/hooks/useVerifyChallenge';
import { getErrorCode } from 'helpers/graphql';

import { modalMinHeight } from '../utils/constant';

export interface ChallengeRequestApprovedProps {
  challengeId: string;
  operation: string;
  userId: string;
  onClose: () => void;
  moveState: (state: ChallengeRequestScreenState) => void;
}

const ChallengeRequestApproved: FC<ChallengeRequestApprovedProps> = ({
  challengeId,
  moveState,
  onClose,
  operation,
  userId,
}) => {
  if (!challengeId || !operation || !userId) {
    throw new Error('Missing challengeId, name, operation or userId');
  }

  const [verificationResult, setVerificationResult] =
    useState<VerifyChallengeOutput | null>(null);

  const { approveConnectedDevice } = useVerifyChallenge();

  useEffect(() => {
    if (!verificationResult) {
      const challengePayload = {
        challengeId,
        name: ChallengeStateName.ChallengeApproved,
        operation,
        userId,
      };
      approveConnectedDevice(challengePayload)
        .then((result) => {
          setVerificationResult(result);
        })
        .catch((error) => {
          const code = getErrorCode(error);
          if (code === 'CHALLENGE_EXPIRED') {
            moveState(ChallengeRequestScreenState.Expired);
          } else {
            moveState(ChallengeRequestScreenState.Error);
          }
        });
    }
  }, [
    challengeId,
    approveConnectedDevice,
    operation,
    userId,
    verificationResult,
    moveState,
  ]);

  if (!verificationResult) {
    return (
      <>
        <SunshineModal.Body alignContent="center" minHeight={modalMinHeight}>
          <Loader />
        </SunshineModal.Body>
        <SunshineModal.Footer flexDirection="row" gap={16}>
          <View marginLeft="75%">
            <Button isDisabled={true} onPress={onClose} variant="secondary">
              <FormattedMessage id="authentication.cancel" />
            </Button>
          </View>
        </SunshineModal.Footer>
      </>
    );
  }

  return (
    <>
      <SunshineModal.Body minHeight={modalMinHeight}>
        <YStack gap={24} paddingBottom="$space.24">
          <YStack alignItems="center">
            <Image
              src={OkHand}
              style={{
                marginBottom: 24,
                opacity: 1,
                width: '30%',
              }}
            />
          </YStack>

          <Typography.Header>
            <FormattedMessage id="authentication.challenge.approved.title" />
          </Typography.Header>
          <Typography.Text>
            <FormattedMessage id="authentication.challenge.approved.description" />
          </Typography.Text>
        </YStack>
      </SunshineModal.Body>
      <SunshineModal.Footer flexDirection="row" gap={16}>
        <View marginLeft="75%">
          <Button onPress={onClose}>
            <FormattedMessage id="authentication.finish" />
          </Button>
        </View>
      </SunshineModal.Footer>
    </>
  );
};

export default ChallengeRequestApproved;
